import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useThread } from '@screentone/addon-auth-wrapper';

import { PageError } from 'contexts/alert/AlertContext';
import { formatAlertMessage, useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { DEFAULT_ALT_SUM_ID } from 'contexts/summarian/SummarianContext';
import { TimeoutError } from 'data/errors/TimeoutError';
import {
  AllessehContentType,
  GroupConfig,
  PageDto,
  PageDtoUpdateInput,
  ScheduledContentCancelInput,
  ScheduledContentCreateInput,
  ScheduledContentRescheduleInput,
  ScheduledContentSaveDraftInput,
  ScheduledContentUpdateInput,
  useCancelScheduledContentMutation,
  useCreateNewPageMutation,
  useGetScheduledContentByTimeWithAllessehIdQuery,
  usePageDtoUpdateMutation,
  useReportFrontendErrorMutation,
  useRescheduleContentMutation,
  useSaveDraftContentMutation,
  useScheduleContentMutation,
  useUpdateScheduledContentMutation
} from 'data/generated/graphql';
import { useScratchpadContext } from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { validateAdFields } from 'utils/adValidation';
import { PAGE_UPDATE_THREAD_PREFIX } from 'utils/constants';
import { formatInTimeZone } from 'utils/dates';
import { validateMultizoneModules, validateSoftLimit } from 'utils/mobile-content';
import { asyncNoop as noop } from 'utils/noop';
import { isWithinTimeInterval, TIMES } from 'utils/time';
import { getUserId } from 'utils/user';

import { Tabs } from 'features/section-page-edit/components/page-multitab-section/pageMultitabSection.types';

type SaveDraftOptions = {
  silent?: boolean; // if true, will not show success alert after saving draft
  takenOverBy?: { id: string; name: string } | null; // user info for who took over the lock, if applicable
};

// import { useSocketNotificationsScheduledContent } from 'hooks/useSocketNotificationsPageContent';

interface PagePublishContext {
  canSave: boolean;
  canPublish: boolean;
  hasPageChanged: boolean;
  tabToChangeTo: Tabs;
  setTabToChangeTo: (tab: Tabs) => void;
  hasRecentlyPublishedScheduledContent: boolean;
  soonToBePublishedScheduledContentPublishUtc: number | null;
  hasRecentlyCreatedCollection: boolean | null;
  hasRecentlyPublished: boolean;
  hasRecentlyScheduled: boolean;
  originalPageWhenInHistoryEditMode: PageDto | null;
  setHasRecentlyCreatedCollection: (hasRecentlyCreatedCollection: boolean) => void;
  handleShowHistoryEditMode: (historyPage: PageDto) => void;
  handleHideHistoryEditMode: () => void;
  handlePublish: (publishUtc?: number) => void;
  isPublishing: boolean;
  handleSaveDraft: (options?: SaveDraftOptions) => Promise<void>;
  isSavingDraft: boolean;
  handleSchedule: (publishUtc: number) => Promise<void>;
  isScheduling: boolean;
  handleReschedule: (publishUtc: number) => Promise<void>;
  isRescheduling: boolean;
  handleUpdateSchedule: () => Promise<void>;
  isUpdatingSchedule: boolean;
  handleDeleteSchedule: () => Promise<void>;
  isDeleting: boolean;
  createNewSection: () => void;
}

const DEFAULT_PAGE_PUBLISH_STATE: PagePublishContext = {
  canSave: false,
  canPublish: false,
  hasPageChanged: false,
  tabToChangeTo: Tabs.ContentSearch,
  setTabToChangeTo: noop,
  hasRecentlyPublishedScheduledContent: false,
  soonToBePublishedScheduledContentPublishUtc: null,
  hasRecentlyCreatedCollection: null,
  hasRecentlyPublished: false,
  hasRecentlyScheduled: false,
  originalPageWhenInHistoryEditMode: null,
  setHasRecentlyCreatedCollection: noop,
  handleShowHistoryEditMode: noop,
  handleHideHistoryEditMode: noop,
  handlePublish: noop,
  isPublishing: false,
  handleSaveDraft: noop,
  isSavingDraft: false,
  handleSchedule: noop,
  isScheduling: false,
  handleReschedule: noop,
  isRescheduling: false,
  handleUpdateSchedule: noop,
  isUpdatingSchedule: false,
  handleDeleteSchedule: noop,
  isDeleting: false,
  createNewSection: noop
};

export const DEFAULT_PAGE_CONFIG = {
  summaryPriority: DEFAULT_ALT_SUM_ID
};

export enum PageUpdateType {
  Publish = 'PAGE_UPDATED',
  Draft = 'DRAFT_UPDATED'
}

export const PagePublishContext = createContext(DEFAULT_PAGE_PUBLISH_STATE);

interface PagePublishProviderProps {
  children: React.ReactNode;
  setData: (data: PageDto) => void;
}

export const PagePublishProvider = ({ children, setData }: PagePublishProviderProps) => {
  const navigate = useNavigate();

  const { alertError, alertSuccess, alertWarning, removeAlert, alertModules } = useAlert();
  const [originalPageWhenInHistoryEditMode, setOriginalPageWhenInHistoryEditMode] = useState<PageDto | null>(null);
  const [hasRecentlyPublished, setHasRecentlyPublished] = useState<boolean>(false); // used to animate in success notification
  const [hasRecentlyScheduled, setHasRecentlyScheduled] = useState<boolean>(false); // used to reset page history
  const [hasRecentlyCreatedCollection, setHasRecentlyCreatedCollection] = useState<boolean>(false);
  // Deferred so window.location.replace fires after canSave re-renders to false, which deregisters
  // the history blocker and prevents the browser's "Leave site?" prompt.
  const [pendingReplacePath, setPendingReplacePath] = useState<string | null>(null);
  const currentProperty = useConvertedProperty();
  const {
    metadata,
    getDTO,
    setNewRoot,
    setNewMetadata,
    hasModelChangedOnce,
    hasModelChangedRecently,
    setHasModelChangedRecently,
    resetModelChanged,
    getSectionAttributes,
    getCancelledEmbargoContent,
    getAllGroupConfigs
  } = useDataModelContext();
  const { currentLayout } = usePageTypeSettingsContext();
  const scratchpadCtx = useScratchpadContext();
  const isCreatingNewPage = useIsNewSectionPage();

  // Helper function to validate Ad fields for the current page
  const validateAdFieldsForCurrentPage = (publicationKey: string): string | null => {
    const sectionAttributes = getSectionAttributes();
    return validateAdFields(sectionAttributes.adPlacementType, sectionAttributes.adZone, publicationKey);
  };

  // Helper function to validate Ad fields and handle errors
  const validateAndAlertAdFields = (publicationKey: string, shouldThrow = false): boolean => {
    const adValidationError = validateAdFieldsForCurrentPage(publicationKey);
    if (adValidationError) {
      alertError(adValidationError);
      if (shouldThrow) {
        throw new Error(adValidationError);
      }
      return false;
    }
    return true;
  };

  const validateAndAlertCancelledEmbargoes = (): boolean => {
    const cancelledItems = getCancelledEmbargoContent();
    if (cancelledItems.length > 0) {
      const ids = cancelledItems.map((item) => item.attributes.id).join(', ');
      alertError(`This page contains cancelled embargoes that must be removed before publishing: ${ids}`);
      return false;
    }
    return true;
  };

  const [canPublish, setCanPublish] = useState<boolean>(false);
  const [canSave, setCanSave] = useState<boolean>(false);
  const [hasRecentlyPublishedScheduledContent, setHasRecentlyPublishedScheduledContent] = useState<boolean>(false);
  const [soonToBePublishedScheduledContentPublishUtc, setSoonToBePublishedScheduledContentPublishUtc] = useState<
    number | null
  >(null);
  const [tabToChangeTo, setTabToChangeTo] = useState<Tabs>(Tabs.ContentSearch);

  const createNewPageMutation = useCreateNewPageMutation();
  const { mutate: reportFrontendError } = useReportFrontendErrorMutation({
    onError: (err) => { console.error('Failed to send Slack error notification:', err); },
  });
  // const [isPreviewDraftPage, setIsPreviewDraftPage] = useState<boolean>(false);

  const oneHourFromNow = useMemo(() => new Date().getTime() + TIMES.ONE_HOUR, []);
  const threadParams = useMemo(
    () => ({ threadId: `${PAGE_UPDATE_THREAD_PREFIX}${metadata.allessehId}`, limit: 1 }),
    [metadata.allessehId]
  );
  const { sendThreadMessage } = useThread(threadParams);
  const { user } = useAuth();
  const userInfo = useMemo(
    () => ({
      id: getUserId(user),
      name: user?.name ?? 'Unknown User'
    }),
    [user]
  );

  const { mutate: pageUpdateMutate, isLoading: isPublishing } = usePageDtoUpdateMutation({
    retry: false, // Disable retry so timeout errors are handled immediately without multiple attempts
    onError: (error: unknown) => {
      if (error instanceof TimeoutError) {
        alertWarning(
          'The page publish request is taking longer than expected. The page may have been published successfully. Please check the publish history or refresh manually to verify.'
        );
        if (currentProperty) {
          reportFrontendError({
            publicationKey: currentProperty,
            errorTitle: 'Page publish timeout',
            errorMessage:
              'The page publish request timed out. The page may have been published successfully.',
            allessehId: metadata.allessehId
          });
        }
        return; // Don't show error message for timeout
      }

      // Handle other errors normally
      const ERROR_MESSAGE = 'Could not publish the page.';
      if (error instanceof Error) {
        if (error.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = error.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${ERROR_MESSAGE} ${error.message}`, { wrapNewlines: true });
        }
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  });
  const { mutateAsync: pageScheduleMutateAsync, isLoading: isScheduling } = useScheduleContentMutation();
  const { mutateAsync: pageRescheduleMutateAsync, isLoading: isRescheduling } = useRescheduleContentMutation();
  const { mutateAsync: pageUpdateScheduleMutateAsync, isLoading: isUpdatingSchedule } =
    useUpdateScheduledContentMutation();
  const { mutateAsync: pageCancelScheduleMutateAsync, isLoading: isDeleting } = useCancelScheduledContentMutation();
  const { mutateAsync: pageSaveDraftMutateAsync, isLoading: isSavingDraft } = useSaveDraftContentMutation();
  // const { data: isPreviewDraftPageResult } = useIsPreviewDraftPageQuery({
  //   allessehId: metadata.allessehId
  // });
  const { data: contentReadyToPublish, refetch: refetchScheduledContentByTime } =
    useGetScheduledContentByTimeWithAllessehIdQuery(
      {
        allessehId: metadata.allessehId,
        maxTimestamp: oneHourFromNow
      },
      {
        enabled: !isCreatingNewPage && !!metadata.allessehId
      }
    );
  const { data: publicationSettingsResponse } = usePublicationSettings();

  const handleShowHistoryEditMode = (historyPage: PageDto) => {
    const page = getDTO() as PageDto;
    setOriginalPageWhenInHistoryEditMode(page);
    setNewRoot(historyPage.root);
    setNewMetadata(historyPage.metadata);
  };

  const handleHideHistoryEditMode = () => {
    if (originalPageWhenInHistoryEditMode) {
      setNewRoot(originalPageWhenInHistoryEditMode.root);
      setNewMetadata(originalPageWhenInHistoryEditMode.metadata);
    }
    setOriginalPageWhenInHistoryEditMode(null);
  };

  const getDTOWithScratchpad = () => {
    const pageDTO = getDTO(true);
    const scratchpad = scratchpadCtx ? scratchpadCtx.getScratchpad(true) : [];
    return {
      ...pageDTO,
      scratchpad
    };
  };

  function createNewSection() {
    removeAlert();

    if (typeof currentProperty !== 'string') {
      alertError('Cannot create section page: no property selected.');
      return;
    }

    const pageDTO = getDTO(true);

    if (!validateAndAlertAdFields(currentProperty)) {
      return;
    }

    const pageCreateInput: PageDtoUpdateInput = {
      pageDTO,
      publicationKey: currentProperty
    };

    createNewPageMutation.mutate(
      {
        pageCreateInput
      },
      {
        onSuccess: (data) => {
          const {
            createNewPage: {
              metadata: { allessehId }
            }
          } = data;

          alertSuccess('Created new section page.');

          navigate(`/${currentProperty}/pages/${allessehId}`); // Redirect to the new section page
        },
        onError: (error: unknown) => {
          if (error instanceof TimeoutError) {
            alertWarning(
              'The page publish request is taking longer than expected. The page may have been published successfully. Please check the publish history or refresh manually to verify.'
            );
            if (currentProperty) {
              reportFrontendError({
                publicationKey: currentProperty,
                errorTitle: 'New page creation timeout',
                errorMessage: `New page creation timed out. Page name: "${pageDTO.metadata.name}". The page may have been created successfully — check the page list to confirm. New page ID is unknown.`,
                allessehId: 'unknown'
              });
            }
            return; // Don't show error message for timeout
          }

          // Handle other errors normally
          let errorMessage = 'Could not publish the page.';

          if (error instanceof Error) {
            errorMessage = error.message;
          }

          alertError(errorMessage);
        }
      }
    );
  }

  const redirectOffVersionUrl = useCallback(() => {
    const versionSegment = /\/version\/\d+$/.exec(window.location.pathname);
    if (versionSegment) {
      setPendingReplacePath(window.location.pathname.slice(0, -versionSegment[0].length));
    }
  }, []);

  const handlePublish = () => {
    removeAlert();

    const initialPageDTO = getDTO();
    const pageDTO = getDTOWithScratchpad();

    if (!validateAndAlertAdFields(currentProperty ?? '')) {
      return;
    }

    if (!validateAndAlertCancelledEmbargoes()) {
      return;
    }

    const multizoneValidationResult = validateMultizoneModules(
      initialPageDTO,
      publicationSettingsResponse?.publicationSetting.rulesConfig
    );

    if (typeof multizoneValidationResult !== 'boolean') {
      const contentTypes = multizoneValidationResult.map((result) => {
        const obj = result as {
          contentType: string;
          zone: string;
        };

        return `${obj.contentType} in ${obj.zone} zone `;
      });

      const pluralize = multizoneValidationResult.length > 1;

      alertError(
        `${pluralize ? 'Content types' : 'Content type'}  ${contentTypes.join(', ')} ${
          pluralize ? 'are' : 'is'
        }  not allowed`
      );
      return;
    }

    const structureGroupConfigs = getAllGroupConfigs();
    // Structure-based pages expose group configs via getAllGroupConfigs(); old-shape pages carry
    // them on currentLayout.groupConfigs. The empty-array return is the sentinel for old-shape.
    const groupConfigsForValidation =
      structureGroupConfigs.length > 0
        ? (structureGroupConfigs as unknown as GroupConfig[])
        : currentLayout?.groupConfigs;
    const softLimitValidation = validateSoftLimit(initialPageDTO, groupConfigsForValidation);

    const updateInput: PageDtoUpdateInput = {
      pageDTO: {
        ...pageDTO,
        scratchpad: scratchpadCtx ? scratchpadCtx.getScratchpad(true) : undefined
      },
      publicationKey: currentProperty ?? ''
    };

    // Use mutate (not mutateAsync) so onSuccess/onError callbacks are properly triggered
    pageUpdateMutate(
      {
        pageUpdateInput: updateInput
      },
      {
        onSuccess: (data) => {
          const { pageDTOUpdate } = data;

          if (softLimitValidation.length > 0) {
            const { errorMessage } = softLimitValidation[0];
            alertWarning(`Published page with warnings: ${errorMessage}`);
          } else {
            alertSuccess('Published page.');
          }

          // Signal other clients listening that the page has been published
          sendThreadMessage({
            message: PageUpdateType.Publish,
            triggeredBy: userInfo
          }).catch(console.error);

          setData(pageDTOUpdate as PageDto);
          setCanPublish(false);
          setCanSave(false);
          // setIsPreviewDraftPage(false);
          resetModelChanged();

          // wait for next tick to allow state to update before signaling publish success
          setTimeout(() => setHasRecentlyPublished(true), 0);

          redirectOffVersionUrl();
        }
      }
    );
  };

  const handleSaveDraft = async (options: SaveDraftOptions = {}) => {
    const { silent = false, takenOverBy = null } = options;
    const pageDTO = getDTOWithScratchpad();

    const input: ScheduledContentSaveDraftInput = {
      allessehId: pageDTO.metadata.allessehId,
      contentType: AllessehContentType.Page,
      body: pageDTO
    };

    try {
      await pageSaveDraftMutateAsync({ publicationKey: currentProperty ?? '', scheduledContentSaveDraftInput: input });
      setCanSave(false);
      setHasModelChangedRecently(false);
      // setIsPreviewDraftPage(true);
      if (!silent) {
        alertSuccess('Saved draft of page.');
      }

      redirectOffVersionUrl();

      // Signal other clients listening that a draft has been saved
      sendThreadMessage({
        message: PageUpdateType.Draft,
        triggeredBy: userInfo,
        takenOverBy
      }).catch(console.error);
    } catch (e: unknown) {
      const ERROR_MESSAGE = 'Could not save draft of the page.';
      if (e instanceof Error) {
        if (e.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = e.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${ERROR_MESSAGE} ${e.message}`, { wrapNewlines: true });
        }
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  };

  const handleSchedule = async (publishUtc: number) => {
    const pageDTO = getDTO(true);

    if (!validateAndAlertAdFields(currentProperty ?? '')) {
      return;
    }

    if (!validateAndAlertCancelledEmbargoes()) {
      return;
    }

    pageDTO.metadata.publishUtc = publishUtc;

    const input: ScheduledContentCreateInput = {
      allessehId: pageDTO.metadata.allessehId,
      contentType: AllessehContentType.Page,
      publishUtc,
      body: pageDTO
    };

    try {
      await pageScheduleMutateAsync({ publicationKey: currentProperty ?? '', scheduledContentCreateInput: input });
      resetModelChanged();
      setHasRecentlyScheduled(true);
      alertSuccess(
        `Scheduled page for ${formatInTimeZone(
          publishUtc,
          publicationSettingsResponse?.publicationSetting.preferredTimezone,
          'MMM d, yyyy h:mm aaa',
          { showTimezoneAbbreviation: true }
        )}.`
      );
      if (soonToBePublishedScheduledContentPublishUtc && soonToBePublishedScheduledContentPublishUtc > publishUtc) {
        setSoonToBePublishedScheduledContentPublishUtc(publishUtc);
      } else if (
        !soonToBePublishedScheduledContentPublishUtc &&
        isWithinTimeInterval(publishUtc, new Date().getTime(), 'hour')
      ) {
        setSoonToBePublishedScheduledContentPublishUtc(publishUtc);
      }
    } catch (e: unknown) {
      const ERROR_MESSAGE = 'Could not schedule the page.';
      if (e instanceof Error) {
        if (e.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = e.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${ERROR_MESSAGE} ${e.message}`, { wrapNewlines: true });
        }
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  };

  const handleReschedule = async (publishUtc: number) => {
    const pageDTO = getDTO(true);

    if (!validateAndAlertAdFields(currentProperty ?? '')) {
      return;
    }

    if (!validateAndAlertCancelledEmbargoes()) {
      return;
    }

    const previousPublishUtc = pageDTO.metadata.publishUtc!;
    pageDTO.metadata.publishUtc = publishUtc;

    const input: ScheduledContentRescheduleInput = {
      allessehId: pageDTO.metadata.allessehId,
      contentType: AllessehContentType.Page,
      previousPublishUtc,
      newPublishUtc: publishUtc,
      body: pageDTO
    };

    try {
      await pageRescheduleMutateAsync({
        publicationKey: currentProperty ?? '',
        scheduledContentRescheduleInput: input
      });
      resetModelChanged();
      setHasRecentlyScheduled(true);
      alertSuccess(
        `Rescheduled page for ${formatInTimeZone(
          publishUtc,
          publicationSettingsResponse?.publicationSetting.preferredTimezone,
          'MMM d, yyyy h:mm aaa',
          { showTimezoneAbbreviation: true }
        )}.`
      );
      handleHideHistoryEditMode();
      await refetchScheduledContentByTime();
    } catch (e: unknown) {
      const ERROR_MESSAGE = 'Could not reschedule the page.';
      if (e instanceof Error) {
        if (e.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = e.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${ERROR_MESSAGE} ${e.message}`, { wrapNewlines: true });
        }
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  };

  const handleUpdateSchedule = async () => {
    const pageDTO = getDTO(true);

    if (!validateAndAlertAdFields(currentProperty ?? '')) {
      return;
    }

    if (!validateAndAlertCancelledEmbargoes()) {
      return;
    }

    const input: ScheduledContentUpdateInput = {
      allessehId: pageDTO.metadata.allessehId,
      publishUtc: pageDTO.metadata.publishUtc!,
      contentType: AllessehContentType.Page,
      body: pageDTO
    };

    try {
      await pageUpdateScheduleMutateAsync({
        publicationKey: currentProperty ?? '',
        scheduledContentUpdateInput: input
      });
      resetModelChanged();
      setHasRecentlyScheduled(true);
      alertSuccess('Scheduled Page has been updated.');
      handleHideHistoryEditMode();
    } catch (e) {
      const ERROR_MESSAGE = 'Could not update scheduled page.';
      if (e instanceof Error) {
        if (e.message.startsWith('PAGE_VALIDATION_ERROR#')) {
          const errorJson = e.message.replace('PAGE_VALIDATION_ERROR#', '');
          const pageError = JSON.parse(errorJson) as PageError;
          alertError(`${ERROR_MESSAGE}\n${formatAlertMessage(pageError)}`, { wrapNewlines: true });
          alertModules(pageError.validationError!);
        } else {
          alertError(`${ERROR_MESSAGE} ${e.message}`, { wrapNewlines: true });
        }
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  };

  const handleDeleteSchedule = async () => {
    const pageDTO = getDTO(true);

    const input: ScheduledContentCancelInput = {
      allessehId: pageDTO.metadata.allessehId,
      publishUtc: pageDTO.metadata.publishUtc!
    };

    try {
      await pageCancelScheduleMutateAsync({
        publicationKey: currentProperty ?? '',
        scheduledContentCancelInput: input
      });
      resetModelChanged();
      setHasRecentlyScheduled(true);
      alertSuccess('Scheduled Page has been deleted.');
      handleHideHistoryEditMode();
      if (
        isWithinTimeInterval(
          soonToBePublishedScheduledContentPublishUtc ?? 0,
          pageDTO.metadata.publishUtc ?? 0,
          'second'
        )
      ) {
        setSoonToBePublishedScheduledContentPublishUtc(null);
      }
    } catch (e: unknown) {
      const ERROR_MESSAGE = 'Could not delete scheduled page.';
      if (e instanceof Error) {
        alertError(`${ERROR_MESSAGE} ${e.message}`);
      } else {
        alertError(ERROR_MESSAGE);
      }
    }
  };

  // const handleScheduledContentPublished = async (publishUtc: number) => {
  //   setSoonToBePublishedScheduledContentPublishUtc(null);
  //   setHasRecentlyPublished(true);
  //   setHasRecentlyPublishedScheduledContent(true);
  //   await refetchScheduledContentByTime();
  //   const formattedDate = formatDateToShortDateTime(publishUtc);
  //   const tabChangeButton = (
  //     <Button
  //       padding={{ all: 'none' }}
  //       margin={{ all: 'none' }}
  //       tertiary
  //       onClick={() => setTabToChangeTo(Tabs.PublishHistory)}
  //     >
  //       View in History
  //     </Button>
  //   );
  //   alertWarning(
  //     <div>
  //       A scheduled publish for {formattedDate} has gone live.{' '}
  //       {canSave || isPreviewDraftPage ? tabChangeButton : 'Refresh the page to see changes.'}
  //     </div>
  //   );
  // };

  // useSocketNotificationsScheduledContent({
  //   canSave,
  //   pageVariant: 'pages',
  //   isPreviewDraftPage,
  //   soonToBePublishedScheduledContentPublishUtc,
  //   setTabToChangeTo,
  //   setHasRecentlyPublished,
  //   setHasRecentlyPublishedScheduledContent,
  //   setSoonToBePublishedScheduledContentPublishUtc,
  //   handleScheduledContentPublished
  // });

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (hasRecentlyPublished) {
      timeout = setTimeout(() => {
        setHasRecentlyPublished(false);
      }, 3000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasRecentlyPublished]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (hasRecentlyPublishedScheduledContent) {
      timeout = setTimeout(() => {
        setHasRecentlyPublishedScheduledContent(false);
      }, 3000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasRecentlyPublishedScheduledContent]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (hasRecentlyScheduled) {
      timeout = setTimeout(() => {
        setHasRecentlyScheduled(false);
      }, 3000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasRecentlyScheduled]);

  useEffect(() => {
    setCanPublish(hasModelChangedOnce);
    setCanSave(hasModelChangedOnce);
  }, [hasModelChangedOnce]);

  useEffect(() => {
    if (hasModelChangedRecently) {
      setCanPublish(true);
      setCanSave(true);
      setHasModelChangedRecently(false);
    }
  }, [hasModelChangedRecently, setCanPublish, setCanSave, setHasModelChangedRecently]);

  useEffect(() => {
    if (pendingReplacePath) {
      window.location.replace(pendingReplacePath);
    }
  }, [pendingReplacePath]);

  // useEffect(() => {
  //   if (isPreviewDraftPageResult) {
  //     // setIsPreviewDraftPage(isPreviewDraftPageResult.isPreviewDraftPage);
  //   }
  // }, [isPreviewDraftPageResult]);

  useEffect(() => {
    if (contentReadyToPublish) {
      const soonToBePublishedContent = contentReadyToPublish.scheduledContentByTimeWithAllessehId;
      if (soonToBePublishedContent.length > 0) {
        setSoonToBePublishedScheduledContentPublishUtc(soonToBePublishedContent[0].publishUtc);
      }
    }
  }, [contentReadyToPublish]);

  const value = {
    hasPageChanged: hasModelChangedOnce,
    hasRecentlyCreatedCollection,
    hasRecentlyPublished,
    hasRecentlyScheduled,
    originalPageWhenInHistoryEditMode,
    setHasRecentlyCreatedCollection,
    handleShowHistoryEditMode,
    handleHideHistoryEditMode,
    handlePublish,
    isPublishing,
    handleSaveDraft,
    isSavingDraft,
    handleSchedule,
    isScheduling,
    handleReschedule,
    isRescheduling,
    handleUpdateSchedule,
    isUpdatingSchedule,
    handleDeleteSchedule,
    isDeleting,
    canSave,
    canPublish,
    hasRecentlyPublishedScheduledContent,
    soonToBePublishedScheduledContentPublishUtc,
    tabToChangeTo,
    setTabToChangeTo,
    createNewSection
  };

  return <PagePublishContext.Provider value={value}>{children}</PagePublishContext.Provider>;
};
